import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ViewMode } from '../../types/api';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import SortOptions from './SortOptions';
import SearchSortOptions from './SearchSortOptions';
import DownloadOptions from './DownloadOptions';
import { isMobileDevice } from '../../utils/platformUtils';
import { useTagBySlug } from '../../hooks/useTagBySlug';
import { useDeleteTag } from '../../services/tagService';
import { useDeleteFolder, useConvertFolderToTag } from '../../services/folders';
import { useCurrentUser } from '../../services/userHooks';
import { useToast } from '../../hooks/useToast';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';

interface ListOptionsPopoverProps {
  mode?: ViewMode;
  closePopover?: () => void;
  folderId?: string;
  tagSlug?: string;
  articleCount?: number;
  onEditFolder?: () => void;
  onEditTag?: () => void;
}

function ListOptionsPopover({
  mode = 'unread',
  closePopover,
  folderId,
  tagSlug,
  articleCount,
  onEditFolder,
  onEditTag,
}: ListOptionsPopoverProps) {
  const { preferences, handlers, getFeatureVisibility } = useDisplaySettings();
  const { showAppearanceOptions, showListOptions } = getFeatureVisibility(mode);
  const tagFromHook = useTagBySlug(tagSlug || '');
  const currentTag = mode === 'tags' && tagSlug ? tagFromHook : undefined;
  const { currentUser } = useCurrentUser();
  const currentFolder = useMemo(() => {
    if (mode === 'folder' && folderId && currentUser?.folders) {
      return currentUser.folders.find((folder) => folder.id === folderId);
    }
    return undefined;
  }, [mode, folderId, currentUser?.folders]);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const deleteTagMutation = useDeleteTag();
  const deleteFolderMutation = useDeleteFolder();
  const convertToTagMutation = useConvertFolderToTag();

  // Determine which modes support download option
  const showDownloadOption =
    ['unread', 'archive', 'liked', 'folder', 'videos', 'tags'].includes(
      mode || 'unread',
    ) && !isMobileDevice();

  const { enableBatchMode } = useBookmarkSelection();
  const isSearch = mode === 'search';
  // Empty tags only get Edit/Delete — no select multiple, sort, or download.
  const isEmptyTag = mode === 'tags' && currentTag && articleCount === 0;
  const showSelectMultiple =
    (showListOptions || isSearch) && !isMobileDevice() && !isEmptyTag;

  const handleSelectMultiple = () => {
    closePopover?.();
    enableBatchMode();
  };

  // Changing the sort dismisses the popover (in every mode). SortOptions only
  // fires this when the order actually changes, so clicking the active option
  // leaves the popover open.
  const handleSortOrderToggle = () => {
    handlers.handleSortOrderToggle();
    closePopover?.();
  };

  const handleEditTag = () => {
    onEditTag?.();
    closePopover?.();
  };

  const handleEditFolder = () => {
    onEditFolder?.();
    closePopover?.();
  };

  const handleDeleteTag = async () => {
    if (!currentTag) return;

    if (!confirm(t`Are you sure you want to delete this tag?`)) {
      return;
    }

    try {
      await deleteTagMutation.mutateAsync({
        tagId: currentTag.id,
      });
      // navigate to home after deletion
      navigate({ to: '/home' });
      closePopover?.();
    } catch (err) {
      // Error handling - could show toast here if needed
      console.error('Failed to delete tag:', err);
    }
  };

  const handleDeleteFolder = async () => {
    if (!currentFolder || !currentUser?.formKey) return;

    if (
      !confirm(
        t`Are you sure you want to delete this folder? All articles will be moved to your home.`,
      )
    ) {
      return;
    }

    try {
      await deleteFolderMutation.mutateAsync({
        folderId: currentFolder.id,
        formKey: currentUser.formKey,
      });
      // navigate to home after deletion
      navigate({ to: '/home' });
      closePopover?.();
    } catch (err) {
      // Error handling - could show toast here if needed
      console.error('Failed to delete folder:', err);
    }
  };

  const handleConvertToTag = async () => {
    if (!currentFolder || !currentUser?.formKey) return;

    if (
      !confirm(
        t`When you convert this folder to a tag...\n* Articles will be given a new tag with this folder name.\n* Articles will be moved to Archive.\n* This folder will be deleted on completion.\n\nThis cannot be undone. Would you like to continue?`,
      )
    ) {
      return;
    }

    try {
      await convertToTagMutation.mutateAsync({
        folderId: currentFolder.id,
        formKey: currentUser.formKey,
      });
      // Show success toast with conversion status message
      showToast(
        t`Folder conversion in progress... it can take a few minutes for conversion to complete.`,
        'success',
        6000,
      );
      // navigate to home after conversion
      navigate({ to: '/home' });
      closePopover?.();
    } catch (err) {
      // Error handling - could show toast here if needed
      console.error('Failed to convert folder to tag:', err);
    }
  };

  return (
    <div className="min-w-48">
      {/* Edit Tag Section */}
      {mode === 'tags' && currentTag && (
        <>
          <button onClick={handleEditTag} className="menu-item">
            <span>
              <Trans>Edit tag</Trans>
            </span>
          </button>
          {showListOptions && !isEmptyTag && <div className="menu-divider" />}
        </>
      )}

      {/* Edit Folder Section */}
      {mode === 'folder' && currentFolder && (
        <>
          <button onClick={handleEditFolder} className="menu-item">
            <span>
              <Trans>Edit Folder</Trans>
            </span>
          </button>
          <button
            onClick={handleConvertToTag}
            disabled={convertToTagMutation.isPending}
            className="menu-item"
          >
            <span>
              {convertToTagMutation.isPending ? (
                <Trans>Converting...</Trans>
              ) : (
                <Trans>Convert to Tag</Trans>
              )}
            </span>
          </button>
          {showListOptions && <div className="menu-divider" />}
        </>
      )}

      {/* Select Multiple + Sort Section */}
      {showSelectMultiple && (
        <>
          <button onClick={handleSelectMultiple} className="menu-item">
            <span>
              <Trans>Select Multiple</Trans>
            </span>
          </button>
          <div className="menu-divider" />
        </>
      )}
      {isSearch ? (
        <SearchSortOptions closePopover={closePopover} />
      ) : (
        showListOptions &&
        !isEmptyTag && (
          <SortOptions
            preferences={preferences}
            onSortOrderToggle={handleSortOrderToggle}
            showAppearanceOptions={showAppearanceOptions}
          />
        )
      )}

      {/* Download Section */}
      {showDownloadOption && !isEmptyTag && (
        <>
          <div className="menu-divider" />
          <DownloadOptions
            mode={mode}
            folderId={folderId}
            tagSlug={tagSlug}
            closePopover={closePopover}
          />
        </>
      )}

      {/* Delete Tag Section */}
      {mode === 'tags' && currentTag && (
        <>
          <div className="menu-divider" />
          <button
            onClick={handleDeleteTag}
            disabled={deleteTagMutation.isPending}
            className="menu-item text-danger hover:text-copy-inverse hover:bg-surface-danger-hover"
          >
            <span>
              {deleteTagMutation.isPending ? (
                <Trans>Deleting...</Trans>
              ) : (
                <Trans>Delete Tag</Trans>
              )}
            </span>
          </button>
        </>
      )}

      {/* Delete Folder Section */}
      {mode === 'folder' && currentFolder && (
        <>
          <div className="menu-divider" />
          <button
            onClick={handleDeleteFolder}
            disabled={deleteFolderMutation.isPending}
            className="menu-item text-danger hover:text-copy-inverse hover:bg-surface-danger-hover"
          >
            <span>
              {deleteFolderMutation.isPending ? (
                <Trans>Deleting...</Trans>
              ) : (
                <Trans>Delete Folder</Trans>
              )}
            </span>
          </button>
        </>
      )}
    </div>
  );
}

export default ListOptionsPopover;
